import { useEffect, useState } from "react";
import { Box, CircularProgress, Alert, Button } from "@mui/material";
import IndexService from "../../Services/http/IndexService";
import { useNavigate } from "react-router";
import { SessionSorageInstance } from "../../Services/SessionStorageService";
import { useTranslation } from "react-i18next";
import OnboardingLayout from "../Layout/OnboardingLayout";

type Status = "loading" | "success" | "error";

const ExternalAwsAuthResult = () => {
    const { t } = useTranslation("externalAwsAuthResult");

    const [status, setStatus] = useState<Status>("loading");
    const [message, setMessage] = useState<string>("");
    const [countdown, setCountdown] = useState<number>(10);
    const account = SessionSorageInstance.getDefaultAccount();
    const isPartner = account === null;
    const navigate = useNavigate();

    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        const spapi_oauth_code = params.get("spapi_oauth_code");
        const state = params.get("state");
        const selling_partner_id = params.get("selling_partner_id");

        if (!spapi_oauth_code || !state || !selling_partner_id) {
            setStatus("error");
            setMessage(t("messages.missingData"));
            return;
        }
        const publicLoginWithAws = async () => {
            return await IndexService.publicLoginWithAws({
                spapi_oauth_code,
                state,
                selling_partner_id,
            });
        };

        const loginWithAws = async () => {
            if (!account) {
                throw new Error("Account is required for loginWithAws");
            }
            return await IndexService.loginWithAws(
                spapi_oauth_code,
                state,
                selling_partner_id,
                account["id"]
            );
        }

        const run = async () => {
            try {
                const res = isPartner ? await publicLoginWithAws() : await loginWithAws();
                if (res && res.message === 'success') {
                    setStatus("success");
                    setMessage(t("messages.connectedSuccessfully"));
                    !isPartner && navigate('/layout')
                } else if (res?.message === "error_login_aws") {
                    setStatus("error");
                    setMessage(t("messages.amazonAuthProblem"));
                } else {
                    setStatus("error");
                    setMessage(t("messages.unexpectedErrorSaving"));
                }
            } catch (err) {
                setStatus("error");
                setMessage(t("messages.technicalError"));
            }
        };

        run();
    }, [isPartner, account, navigate]);

    useEffect(() => {
        if (status !== "success" || !isPartner) return;

        const interval = setInterval(() => {
            setCountdown((prev) => {
                if (prev <= 1) {
                    clearInterval(interval);
                    window.close();
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);

        return () => clearInterval(interval);
    }, [status, isPartner]);

    const handleClose = () => {
        window.close();
    };

    return (
        <OnboardingLayout rightClassName="text-center pr-3 pl-3 pb-2 m-auto f-18">
            <h2>{t("title")}</h2>

            {status === "loading" && (
                <Box mt={3}>
                    <CircularProgress />
                    <p style={{ marginTop: 16 }}>{t("loadingText")}</p>
                </Box>
            )}

            {status === "success" && (
                <Box mt={3}>
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {message}
                    </Alert>

                    {isPartner ? (
                        <p>
                            {t("success.partnerReturn")}
                            <br />
                            {t("success.autoClose", { countdown })}
                        </p>
                    ) : (
                        <p>{t("success.nonPartnerReturn")}</p>
                    )}

                    <Button
                        variant="contained"
                        className="button-login h-40p w-200p"
                        onClick={handleClose}
                        sx={{ mt: 2 }}
                    >
                        {t("buttons.closeWindow")}
                    </Button>
                </Box>
            )}

            {status === "error" && (
                <Box mt={3}>
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {message}
                    </Alert>
                    {isPartner ? (
                        <>
                            <p>{t("error.partnerHint")}</p>
                            <Button
                                variant="contained"
                                className="button-login h-40p w-200p"
                                onClick={handleClose}
                                sx={{ mt: 2 }}
                            >
                                {t("buttons.close")}
                            </Button>
                        </>
                    ) : (
                        <>
                            <p>{t("error.nonPartnerHint")}</p>
                            <Button
                                variant="contained"
                                className="button-login h-40p w-200p"
                                onClick={() => navigate("/appApproval")}
                                sx={{ mt: 2 }}
                            >
                                {t("buttons.authorizeAgain")}
                            </Button>
                        </>
                    )}
                </Box>
            )}
        </OnboardingLayout>
    );
};

export default ExternalAwsAuthResult;